import React, { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread, Agent } from '@dreamai/types';
import './conversation-list.scss';
import { ReactComponent as DotsVerticalIcon } from '../../../icons/dots-vertical.svg';
import { generateMessageColors } from './threadUtils';
import { ReactComponent as WhatsappIcon } from '../../../icons/whatsapp.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as PlanIcon } from '../../../icons/plan.svg';
import { ReactComponent as ShareIcon } from '../../../icons/share.svg';
import { ReactComponent as DownCaretIcon } from '../../../icons/down-caret.svg';
import { ReactComponent as RightCaretIcon } from '../../../icons/right-caret.svg';
import { ReactComponent as ChatBubbleIcon } from '../../../icons/chat-bubble.svg';
import { useMessaging } from '../../../contexts/MessagingContext';
import { useSidebarDrawer } from '../../../contexts/SidebarDrawerContext';
import { useThreadReads } from '../../../providers';
import BackgroundThreadButton from './InnerMonologueThreadButton';
import { useAgent } from '../../../contexts/AgentContext';
import { formatAgentLabel } from './threadUtils';
import UnreadIndicator from './UnreadIndicator';
import ThreadItem from './ThreadItem';

interface ConversationListProps {
  threads: AgentThread[];
  selectedThreadId?: string | null;
  onThreadSelect: (
    threadId: string,
    agentId?: string,
    readMarker?: string,
  ) => void;
}

interface GroupedThreads {
  [key: string]: AgentThread[];
}

const ConversationList: FC<ConversationListProps> = ({
  threads,
  selectedThreadId,
  onThreadSelect,
}) => {
  const { user } = useAuth0();
  const userId = user?.sub;

  // Get agents from context
  const { agents } = useAgent();

  // Get messaging state from context
  const { messagingState } = useMessaging();
  const whatsappConnected = messagingState.whatsapp.connected;

  // Get unread threads from ThreadReadsProvider
  const { getAllUnreadThreads } = useThreadReads();
  const unreadThreads = getAllUnreadThreads();
  const unreadThreadIds = new Set(unreadThreads.map((t) => t.thread_id));

  // Create map of thread_id -> last_read_at
  const threadReadMap = useMemo(() => {
    return new Map(unreadThreads.map((t) => [t.thread_id, t.last_read_at]));
  }, [unreadThreads]);

  // Helper to create read marker: base64(userId:lastReadAt)
  const createReadMarker = useCallback(
    (threadId: string): string | undefined => {
      if (!userId) return undefined;
      const lastReadAt = threadReadMap.get(threadId);
      if (!lastReadAt) return undefined;

      const data = JSON.stringify({ userId, lastReadAt });
      return btoa(data); // base64 encode
    },
    [userId, threadReadMap],
  );

  // State for tracking which groups are expanded (show more functionality)
  // Load initial state from localStorage
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('conversationListExpandedGroups');
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (error) {
      // Ignore localStorage errors
    }
    return new Set();
  });

  // Get drawer state from context
  const { openDrawer, setOpenDrawer, setDrawerThreads } = useSidebarDrawer();

  // Save expanded groups to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        'conversationListExpandedGroups',
        JSON.stringify(Array.from(expandedGroups)),
      );
    } catch (error) {
      // Ignore localStorage errors
    }
  }, [expandedGroups]);

  // Check if background threads should be shown (im=true in URL)
  const showBackgroundThreads = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('im') === 'true';
  }, []);

  // Group threads by type
  // Separate main threads, background threads, and regular threads
  // Main threads only shown if includeInHeadings is true (belongs to current user/company)
  // Archived threads (rotated long-running threads) are hidden from all lists
  const mainThreads = threads.filter(
    (thread) =>
      thread.is_main_thread &&
      thread.include_in_headings &&
      !thread.is_archived_thread,
  );
  // Helper to check if thread has ExpectedCall discussing object
  const hasExpectedCallDiscussingObject = (thread: AgentThread) =>
    thread.discussing_objects?.some(
      (obj) => obj.discussing_object === 'ExpectedCall',
    );

  const backgroundThreads = threads.filter(
    (thread) =>
      !thread.is_archived_thread &&
      (thread.is_background ||
        thread.is_internal_monologue ||
        thread.is_learning_thread ||
        hasExpectedCallDiscussingObject(thread)),
  );
  const regularThreads = threads.filter(
    (thread) =>
      !thread.is_archived_thread &&
      (!thread.is_main_thread || !thread.include_in_headings) &&
      !thread.is_background &&
      !thread.is_internal_monologue &&
      !thread.is_learning_thread &&
      !hasExpectedCallDiscussingObject(thread),
  );

  const groupThreadsByDiscussingObject = (
    threads: AgentThread[],
  ): GroupedThreads => {
    const groupedThreads: GroupedThreads = {};

    threads.forEach((thread) => {
      // Check if this is a shared thread (via_shared but not via_company)
      if (thread.via_shared && !thread.via_company) {
        // Add to "Shared with me" group
        if (!groupedThreads['Shared with me']) {
          groupedThreads['Shared with me'] = [];
        }
        groupedThreads['Shared with me'].push(thread);
      } else if (
        thread.discussing_objects &&
        thread.discussing_objects.length > 0
      ) {
        // If thread has discussing objects, add it to each group
        thread.discussing_objects.forEach((discussingObj) => {
          const key = discussingObj.discussing_object;
          if (!groupedThreads[key]) {
            groupedThreads[key] = [];
          }
          // Only add if not already in this group (avoid duplicates)
          if (!groupedThreads[key].find((t) => t.id === thread.id)) {
            groupedThreads[key].push(thread);
          }
        });
      } else {
        // Threads without discussing objects go to "Side Chats" group
        if (!groupedThreads['Side Chats']) {
          groupedThreads['Side Chats'] = [];
        }
        groupedThreads['Side Chats'].push(thread);
      }
    });

    // Sort threads within each group
    Object.keys(groupedThreads).forEach((key) => {
      // Special sorting for Campaigns: by status (Live > Paused > Archived) then alphabetically
      if (key === 'Campaign' || key === 'Campaigns') {
        groupedThreads[key].sort((a, b) => {
          // Define status priority
          const statusOrder = { Live: 0, Paused: 1, Archived: 2 };

          // Get status from pills
          const getStatus = (thread: AgentThread) => {
            const statusPill = thread.discussing_object_pills?.find((pill) =>
              ['Live', 'Paused', 'Archived'].includes(pill.value),
            );
            return statusPill?.value || 'Archived'; // Default to Archived if no status
          };

          // Check unread status
          const aUnread = unreadThreadIds.has(a.id);
          const bUnread = unreadThreadIds.has(b.id);

          // Primary sort: unread first
          if (aUnread && !bUnread) return -1;
          if (!aUnread && bUnread) return 1;

          const statusA = getStatus(a);
          const statusB = getStatus(b);

          // Secondary sort: by status
          const statusDiff =
            (statusOrder[statusA as keyof typeof statusOrder] ?? 99) -
            (statusOrder[statusB as keyof typeof statusOrder] ?? 99);

          if (statusDiff !== 0) {
            return statusDiff;
          }

          // Tertiary sort: alphabetically by description
          const nameA = a.description || '';
          const nameB = b.description || '';
          return nameA.localeCompare(nameB);
        });
      } else {
        // Default sorting: unread first, then by last_updated_at
        groupedThreads[key].sort((a, b) => {
          // Check unread status
          const aUnread = unreadThreadIds.has(a.id);
          const bUnread = unreadThreadIds.has(b.id);

          // Primary sort: unread first
          if (aUnread && !bUnread) return -1;
          if (!aUnread && bUnread) return 1;

          // Secondary sort: by last_updated_at
          return (
            new Date(b.last_updated_at).getTime() -
            new Date(a.last_updated_at).getTime()
          );
        });
      }
    });
    return groupedThreads;
  };

  const groupedThreads = groupThreadsByDiscussingObject(regularThreads);

  // Calculate how many threads to show per group (distribute 12 threads smartly)
  const threadsToShowPerGroup = useMemo(() => {
    const MAX_THREADS = 12;
    const groupEntries = Object.entries(groupedThreads).sort(([a], [b]) => {
      // Same sort order as display
      if (a === 'Shared with me') return -1;
      if (b === 'Shared with me') return 1;
      if (a === 'Side Chats') return 1;
      if (b === 'Side Chats') return -1;
      return a.localeCompare(b);
    });

    const result: Record<string, number> = {};
    let remainingThreads = MAX_THREADS;
    let remainingGroups = groupEntries.length;

    // First pass: allocate threads, using full count for groups with fewer threads
    groupEntries.forEach(([groupName, threads]) => {
      const evenShare = Math.round(remainingThreads / remainingGroups);
      const allocated = Math.min(threads.length, evenShare);
      result[groupName] = allocated;
      remainingThreads -= allocated;
      remainingGroups--;
    });

    return result;
  }, [groupedThreads]);

  // Toggle group expansion (show more functionality)
  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  // Toggle drawer open/close
  const toggleDrawer = (groupName: string) => {
    const threadsInGroup = groupedThreads[groupName] || [];
    if (openDrawer === groupName) {
      setOpenDrawer(null);
      setDrawerThreads([]);
    } else {
      setOpenDrawer(groupName);
      setDrawerThreads(threadsInGroup);
    }
  };

  // Get icon for discussing object
  const getDiscussingObjectIcon = (
    discussingObject: string,
  ): React.ReactNode | null => {
    const singular = discussingObject.toLowerCase();

    if (singular === 'shared with me') {
      return <ShareIcon />;
    } else if (singular === 'campaign' || singular === 'campaigns') {
      return <CampaignIcon />;
    } else if (singular === 'plan' || singular === 'plans') {
      return <PlanIcon />;
    } else if (singular === 'side chats') {
      return <ChatBubbleIcon />;
    }

    // Default fallback for anything else
    return <ChatBubbleIcon />;
  };

  // Pluralize discussing object names
  const pluralize = (word: string): string => {
    // Don't pluralize special group names
    if (word === 'Side Chats' || word === 'Shared with me') return word;

    // Simple pluralization rules
    if (
      word.endsWith('s') ||
      word.endsWith('x') ||
      word.endsWith('z') ||
      word.endsWith('ch') ||
      word.endsWith('sh')
    ) {
      return word + 'es';
    } else if (word.endsWith('y') && !/[aeiou]y$/i.test(word)) {
      return word.slice(0, -1) + 'ies';
    } else {
      return word + 's';
    }
  };

  // Format the thread date/time
  const formatThreadTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const threadDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    // If today, just show time
    if (threadDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    // Otherwise show full date with time
    const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${dayName}, ${day} ${month} ${year}. ${time}`;
  };

  // Get agent for a thread
  const getAgentForThread = (agentId: string): Agent | undefined => {
    return agents.find((a) => a.id === agentId);
  };

  // Get agent color styling if available
  const getThreadStyles = (agentId: string | undefined) => {
    if (!agentId) return {};
    const agent = getAgentForThread(agentId);
    if (!agent?.predominant_colour) return {};

    const colors = generateMessageColors(agent.predominant_colour);
    return {
      boxShadow: colors.boxShadow,
      background: colors.background,
    };
  };

  // Get agent name for main thread display
  const getAgentName = (agentId: string | undefined): string => {
    if (!agentId) return 'Agent';
    const agent = getAgentForThread(agentId);
    if (!agent) return 'Agent';
    return formatAgentLabel(agent);
  };

  return (
    <>
      <div className='conversation-list-container'>
        <div className='scrollable-area'>
          {/* Display main threads and background threads in a separate section */}
          {(mainThreads.length > 0 ||
            (showBackgroundThreads && backgroundThreads.length > 0)) && (
            <div className='favorites-area'>
              <ul className='thread-list'>
                {mainThreads.map((thread) => {
                  const isUnread = unreadThreadIds.has(thread.id);
                  const agent = thread.agent_id
                    ? getAgentForThread(thread.agent_id)
                    : null;
                  return (
                    <li
                      key={thread.id}
                      className={`thread-item-wrapper ${isUnread ? 'unread' : ''}`}
                    >
                      <a
                        href={`?agent_id=${thread.agent_id}&thread_id=${thread.id}`}
                        className={`thread-item main-thread ${selectedThreadId === thread.id ? 'active' : ''}`}
                        onClick={(e) => {
                          // Only prevent default if not opening in new tab
                          if (
                            !e.ctrlKey &&
                            !e.metaKey &&
                            !e.shiftKey &&
                            e.button === 0
                          ) {
                            e.preventDefault();
                            onThreadSelect(
                              thread.id,
                              thread.agent_id,
                              createReadMarker(thread.id),
                            );
                            // Close the drawer if it's open
                            if (openDrawer) {
                              setOpenDrawer(null);
                              setDrawerThreads([]);
                            }
                          }
                        }}
                        style={getThreadStyles(thread.agent_id)}
                      >
                        <div className='agent-avatar'>
                          {agent?.image_url && (
                            <img
                              src={agent.image_url}
                              alt={getAgentName(thread.agent_id)}
                            />
                          )}
                        </div>
                        <div className='text-wrapper'>
                          <div className='thread-title'>
                            {getAgentName(thread.agent_id)}
                          </div>
                        </div>
                        <div className='unread-notification-area'>
                          {isUnread && <UnreadIndicator />}
                        </div>
                      </a>
                    </li>
                  );
                })}
                {/* Background Threads (appears when im=true in URL) */}
                {showBackgroundThreads &&
                  backgroundThreads.map((thread) => (
                    <BackgroundThreadButton
                      key={thread.id}
                      threads={[thread]}
                      selectedThreadId={selectedThreadId}
                      onThreadSelect={onThreadSelect}
                      visible={showBackgroundThreads}
                    />
                  ))}
              </ul>
            </div>
          )}

          {/* Display regular threads grouped by discussing object */}
          {regularThreads.length === 0 && mainThreads.length === 0 ? (
            <div className='no-conversations'>No conversations yet</div>
          ) : (
            <div className='thread-groups-list'>
              {Object.entries(groupedThreads)
                .sort(([a], [b]) => {
                  // Explicit order: Campaign, Plan, Shared With Me, Side Chats, then alphabetically
                  const order: Record<string, number> = {
                    Campaign: 1,
                    Plan: 2,
                    'Shared with me': 3,
                    'Side Chats': 4,
                  };

                  const aOrder = order[a] || 999;
                  const bOrder = order[b] || 999;

                  if (aOrder !== bOrder) {
                    return aOrder - bOrder;
                  }

                  // If both are not in the priority list, sort alphabetically
                  return a.localeCompare(b);
                })
                .map(([discussingObject, threadsInGroup]) => {
                  const isOpen = openDrawer === discussingObject;
                  const unreadCount = threadsInGroup.filter((thread) =>
                    unreadThreadIds.has(thread.id),
                  ).length;
                  return (
                    <div
                      key={discussingObject}
                      className={`timeframe-group ${isOpen ? 'active' : ''}`}
                      onClick={() => toggleDrawer(discussingObject)}
                      style={{ cursor: 'pointer' }}
                    >
                      <h5>
                        {getDiscussingObjectIcon(discussingObject)}
                        {pluralize(discussingObject)}
                        {unreadCount > 0 && (
                          <span className='unread-badge'>{unreadCount}</span>
                        )}
                      </h5>
                      <RightCaretIcon className='caret-icon' />
                    </div>
                  );
                })}
            </div>
          )}

          {/* Unread threads section */}
          {regularThreads.filter((thread) => unreadThreadIds.has(thread.id))
            .length > 0 && (
            <div className='unread-threads-section'>
              <h5 className='section-heading'>Unread chats</h5>
              <ul className='thread-list'>
                {regularThreads
                  .filter((thread) => unreadThreadIds.has(thread.id))
                  .map((thread) => {
                    const discussingObject =
                      thread.discussing_objects?.[0]?.discussing_object ||
                      'Side Chats';
                    return (
                      <ThreadItem
                        key={thread.id}
                        thread={thread}
                        selectedThreadId={selectedThreadId}
                        onThreadSelect={onThreadSelect}
                        discussingObject={discussingObject}
                        isUnread={true}
                        createReadMarker={createReadMarker}
                        getDiscussingObjectIcon={getDiscussingObjectIcon}
                        onSelect={() => {
                          if (openDrawer) {
                            setOpenDrawer(null);
                            setDrawerThreads([]);
                          }
                        }}
                      />
                    );
                  })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConversationList;
